// src/components/ModalFormFinanciera.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import "animate.css";

import { putFinanciera as modifFinanciera } from "../helpers/rutaFinancieras";

/* ---------------- utils ---------------- */

const clean = (v) => String(v ?? "").trim();

const initial = {
  codfinanciera: "",
  financiera: "",
  razonsocial: "",
  cuit: "",
  domicilio: "",
  telefono1: "",
  email: "",
  contacto: "",
  observaciones: "",
};

/* ---------------- componente ---------------- */

export default function ModalFormFinanciera({
  financiera,
  handleClose,
  show,
  onSaved,
}) {
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ---------------- hidratar financiera ---------------- */

  useEffect(() => {
    if (!show || !financiera) return;

    const next = {
      codfinanciera: clean(financiera?.codfinanciera),
      financiera: clean(financiera?.financiera ?? financiera?.nombre),
      razonsocial: clean(financiera?.razonsocial),
      cuit: clean(financiera?.cuit),
      domicilio: clean(financiera?.domicilio),
      telefono1: clean(financiera?.telefono1 ?? financiera?.telefono),
      email: clean(financiera?.email),
      contacto: clean(financiera?.contacto),
      observaciones: clean(financiera?.observaciones),
    };

    setFormValues(next);
  }, [financiera, show]);

  /* ---------------- validate / payload ---------------- */

  const validate = () => {
    if (!clean(formValues.financiera)) {
      return "Ingresá el nombre de la financiera.";
    }

    const email = clean(formValues.email);

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Ingresá un email válido.";
    }

    return "";
  };

  const buildPayload = () => {
    const payload = {
      codfinanciera: clean(formValues.codfinanciera),

      financiera: clean(formValues.financiera),
      razonsocial: clean(formValues.razonsocial),
      cuit: clean(formValues.cuit),
      domicilio: clean(formValues.domicilio),

      telefono1: clean(formValues.telefono1),
      email: clean(formValues.email),
      contacto: clean(formValues.contacto),

      observaciones: clean(formValues.observaciones),

      activo: true,
    };

    if (!clean(payload.codfinanciera)) {
      delete payload.codfinanciera;
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();

    if (msg) return alert(msg);

    const financieraId = financiera?._id || financiera?.id;

    if (!financieraId) {
      return alert("No se encontró el ID de la financiera a modificar.");
    }

    const payload = buildPayload();

    console.log("📤 PAYLOAD MODAL FINANCIERA:", payload);

    try {
      setSaving(true);

      const resp = await modifFinanciera(financieraId, payload);

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);

        if (typeof onSaved === "function") {
          onSaved(resp);
        }

        handleClose();
      }, 900);
    } catch (error) {
      console.error(
        "❌ Error al actualizar financiera:",
        error?.response?.data || error
      );

      const mensaje =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "No se pudo actualizar la financiera.";

      alert(mensaje);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
        scrollable
      >
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Header closeButton>
            <Modal.Title>Modificar Financiera</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="container-fluid">
              {/* ---------------- fila 1 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-2">
                  <label>Código</label>
                  <input
                    className="form-control"
                    name="codfinanciera"
                    value={formValues.codfinanciera}
                    disabled
                  />
                </div>

                <div className="form-group mt-2 col-md-5">
                  <label>Financiera</label>
                  <input
                    className="form-control"
                    name="financiera"
                    value={formValues.financiera}
                    onChange={handleChange}
                    placeholder="Nombre de la financiera"
                  />
                </div>

                <div className="form-group mt-2 col-md-5">
                  <label>Razón Social</label>
                  <input
                    className="form-control"
                    name="razonsocial"
                    value={formValues.razonsocial}
                    onChange={handleChange}
                    placeholder="Razón social"
                  />
                </div>
              </div>

              {/* ---------------- fila 2 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-4">
                  <label>CUIT</label>
                  <input
                    className="form-control"
                    name="cuit"
                    value={formValues.cuit}
                    onChange={handleChange}
                    placeholder="CUIT"
                  />
                </div>

                <div className="form-group mt-2 col-md-8">
                  <label>Domicilio</label>
                  <input
                    className="form-control"
                    name="domicilio"
                    value={formValues.domicilio}
                    onChange={handleChange}
                    placeholder="Domicilio"
                  />
                </div>
              </div>

              {/* ---------------- fila 3 ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-4">
                  <label>Teléfono</label>
                  <input
                    className="form-control"
                    name="telefono1"
                    value={formValues.telefono1}
                    onChange={handleChange}
                    placeholder="Teléfono"
                  />
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                    placeholder="financiera@email.com"
                  />
                </div>

                <div className="form-group mt-2 col-md-4">
                  <label>Contacto</label>
                  <input
                    className="form-control"
                    name="contacto"
                    value={formValues.contacto}
                    onChange={handleChange}
                    placeholder="Persona de contacto"
                  />
                </div>
              </div>

              {/* ---------------- observaciones ---------------- */}
              <div className="row">
                <div className="form-group mt-2 col-md-12">
                  <label>Observaciones</label>
                  <textarea
                    className="form-control"
                    name="observaciones"
                    value={formValues.observaciones}
                    onChange={handleChange}
                    placeholder="Observaciones"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>

            <Button type="submit" variant="dark" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" /> Guardando...
                </>
              ) : (
                "Guardar"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ---------------- Modal éxito ---------------- */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cambios guardados correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}